import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import { useForm } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type CoDebtorFormValue,
	getCoDebtorDrawerShape,
} from "web_myplann/Portfolio/Forms/CoDebtors/coDebtorsSchema";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

const PageResources = Resources.Portfolio.CoDebtors;

type Props = {
	coDebtor: CoDebtorFormValue;
	otherIdentifications: string[];
	onSave: (coDebtor: CoDebtorFormValue) => void;
	onRemove?: () => void;
	onClose: () => void;
};

export const CoDebtorDrawer = ({
	coDebtor,
	otherIdentifications,
	onSave,
	onRemove,
	onClose,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const {
		control,
		handleSubmit,
		getValues,
		formState: { isDirty },
	} = useForm({
		resolver: zodResolver(getCoDebtorDrawerShape(t, otherIdentifications)),
		defaultValues: coDebtor,
	});

	const handleSave = handleSubmit(() => onSave(getValues()));

	return (
		<MyPlannDrawer
			anchor="bottom"
			open
			onClose={onClose}
			title={t(PageResources.drawerTitle)}
			grabber
		>
			<Stack pb={theme.spacingTokens.lg}>
				<FlatRowFormInput
					control={control}
					name="firstname"
					label={t(PageResources.firstname)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="lastname"
					label={t(PageResources.lastname)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="identification"
					label={t(PageResources.identification)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					isLast
				/>
				<Stack
					px={theme.spacingTokens.lg}
					pt={theme.spacingTokens.lg}
					gap={theme.spacingTokens.sm}
				>
					<Button variant="primary" onClick={handleSave} disabled={!isDirty}>
						{t(PageResources.save)}
					</Button>
					{onRemove && (
						<Button
							variant="ghost"
							color="error"
							startIcon={<CircleMinus size={theme.iconSize.sm} />}
							onClick={onRemove}
						>
							{t(PageResources.remove)}
						</Button>
					)}
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
